import { useQuery } from "./urqlHooks";

const _DOCUMENTS = `
query GET_PAGINATED_DOCUMENTS($input: PaginatedInput!) {
    paginatedDocumentsByCompany(input: $input) {
      totalDocs
      docs {
        id
        currencyType
        companyId
        name
        amount
        documentDate
        isHidden
        isManual
        macroCategory
        category
        eerrArea
        fieldKey
        documentType
        dueDate
        originalDueDate
        cashflowAreaId
        cashflowArea {
          id
          name
        }
        company {
          id
          name
        }
        meta {
          ... on TaxDocumentMeta {
            documentId
            documentCode
            originalAmount
            netAmount
            downloadURL
            download {
              pdf {
                url
              }
              xml {
                url
              }
              html {
                url  
              }
            }
            state
            taxAmount
            otherTaxes {
                name
                value
                taxRate
                code
              }
            refDocuments {
              documentId
              documentCode
            }  
            description {
              item
              name
              quantity
              description
              unit
              unit_price
              total_price
              discount
              discount_percentage
            }
            issuedAt
            dueDate
            isPaid
            isUncollectible
            isReceived
            paymentDate
            issuerId
            issuerName
            receiverId
            receiverName
            taxRate
            remaining
            summary
            period
            isValid
            inform {
             amountInform
             isInform
             remaining
             informDate
            }
            matchs {
              inMatch {
                id
                amount
                selected
                matchSource
                origin
                executionId
                ruleId
                userRelated {
                  userId
                  firstName
                  lastName
                  date
                  email
                }
                accountPlan {
                  level1 {
                    value
                    label
                  }
                  level2 {
                    value
                    label
                  }
                  level3 {
                    value
                    label
                  }
                }
                entries {
                  completed
                }
              }
              outMatch {
                id
                amount
                selected
                matchSource
                origin
                executionId
                ruleId
                userRelated {
                  userId
                  firstName
                  lastName
                  date
                  email
                }
                accountPlan {
                  level1 {
                    value
                    label
                  }
                  level2 {
                    value
                    label
                  }
                  level3 {
                    value
                    label
                  }
                }
                entries {
                  completed
                }
              }
            }
            nominaStatus {
              inNominaProcess {
                nominaName
              }
            }
          }
        }
        meta {
        ... on ObligationMeta {
          family
          familyLabel
          type
          typeLabel
          description
          date
          documentId
          number
          amount
          currency
          isPaid
          remaining
          originalAmount
          documentDate
          taxRate
          rate
          referenceNumber
          issuerId
          issuerName
          receiverId
          receiverName
          isValid
          isReceived
          exchange {
            originalAmount
            originalCurrency
            localAmount
            localCurrency
            exchangeRate
            exchangeDate
          }
          matchs {
            inMatch {
              id
              amount
              selected
              userRelated {
                userId
                firstName
                lastName
                date
              }
            }
            outMatch {
              id
              amount
              selected
              userRelated {
                userId
                firstName
                lastName
                date
              }
            }
          }
          nominaStatus {
            inNominaProcess {
              nominaName
            }
          }
        }
      }
      meta {
        ... on CreditsMeta {
          installments
          interest
          stock
          totalPayments
          paymentNumber
          isPaid
          identificator
        }
      }
      }
    }
  } `;

const DOCUMENTS_STRING = `
query GET_PAGINATED_DOCUMENTS($input: PaginatedInput!) {
  paginatedDocumentsByCompanyAsString(input: $input)
}`

export const GetDocumentsByCompanies = (input = {}) => {
  const { newFilter } = input;

  if (newFilter) {
    const date = [];

    if (newFilter?.startDate) date[0] = newFilter.startDate;
    if (newFilter?.endDate) date[1] = newFilter.endDate;

    if (newFilter?.startDate && newFilter?.endDate)
      input.newFilter = { ...input.newFilter, ...{ date } };
  }

  delete input.newFilter?.startDate;
  delete input.newFilter?.endDate;
  delete input.newFilter?.rate; // Remove rate - it's not a valid field in TaxDocumentNewFilter (it's a meta property, not a filter)

  if (input.newFilter?.dates) {
    if (!input.newFilter?.dates[0]) delete input.newFilter.dates;
  }

  // Filter out invalid fields from newFilter that aren't in TaxDocumentNewFilter
  // Valid fields: rut, filterRut, name, docNum, status, isPaid, notPaid, notAccepted, inform,
  // amountStart, amountEnd, dateStart, dateEnd, dates, type, id, delayed, documentCodes,
  // remaining, classified, receiverName, eerrArea, obligationTypes, excludeObligationTypes,
  // EERRAreas, isHidden, currencyType, documentType, cashflowAreaId, isAccounting, accountingStatus
  const validFields = new Set([
    'rut', 'filterRut', 'name', 'docNum', 'status', 'isPaid', 'notPaid', 'notAccepted', 'inform',
    'amountStart', 'amountEnd', 'dateStart', 'dateEnd', 'dates', 'type', 'id', 'delayed', 'documentCodes',
    'remaining', 'classified', 'receiverName', 'eerrArea', 'obligationTypes', 'excludeObligationTypes',
    'EERRAreas', 'isHidden', 'currencyType', 'documentType', 'cashflowAreaId', 'isAccounting', 'accountingStatus',
    'hasInnerComments', 'isReported', 'bhView',
  ]);

  if (input.newFilter) {
    const filteredNewFilter = {};
    const removedFields = [];
    
    for (const key in input.newFilter) {
      if (validFields.has(key)) {
        filteredNewFilter[key] = input.newFilter[key];
      } else {
        removedFields.push(key);
      }
    }

    input.newFilter = filteredNewFilter;
  }

  const [result, reexecuteQuery] = useQuery({
    query: DOCUMENTS_STRING,
    variables: { input },
  });
  const { data, fetching, error } = result;
  return { data, fetching: !input ? false : fetching, error, reexecuteQuery };
};

const POSSIBLE_MATCHS = `
query POSSIBLE_MATCHS($input: PaginatedInput!) {
  paginatedPossibleMatchsByDocument(input: $input) {
      totalDocs
      docs {
        id
        currencyType
        companyId
        name
        amount
        documentDate
        documentType
        meta {
          ... on TaxDocumentMeta {
            documentId
            originalAmount
            isPaid
            isReceived
            issuerId
            issuerName
            receiverId
            receiverName
            remaining
            summary
            matchs {
              inMatch {
                id
                amount
                selected
                entries {
                  completed
                }
              }
            }
          }
        }
        meta {
        ... on ObligationMeta {
          type
          typeLabel
          number
          remaining
          originalAmount
          issuerId
          issuerName
          receiverId
          receiverName
          rate
          isReceived
          isPaid
          exchange {
            originalAmount
            originalCurrency
            localAmount
            localCurrency
            exchangeRate
          }
          matchs {
            inMatch {
              id
              amount
              selected
            }
          }
        }
      }
      meta {
        ... on CreditsMeta {
          paymentNumber
          isPaid
          identificator
          remaining
          creditType
          originalAmount
        }
      }
      }
    }
  } `;

export const GetPossibleMatchDocuments = (input = {}) => {
  const safeInput = {
    ...input,
    newFilter: input?.newFilter ? { ...input.newFilter } : undefined,
  };
  const { newFilter } = safeInput;

  if (newFilter) {
    const date = [];

    if (newFilter?.startDate) date[0] = newFilter.startDate;
    if (newFilter?.endDate) date[1] = newFilter.endDate;

    if (newFilter?.startDate && newFilter?.endDate)
      safeInput.newFilter = { ...newFilter, date };
  }

  if (safeInput.newFilter) {
    delete safeInput.newFilter.startDate;
    delete safeInput.newFilter.endDate;
    if (safeInput.newFilter.dates && !safeInput.newFilter.dates[0]) {
      delete safeInput.newFilter.dates;
    }
  }

  const pause = !safeInput?.companyId || !safeInput?.pagination?.limit;
  const [result, reexecuteQuery] = useQuery({
    query: POSSIBLE_MATCHS,
    variables: { input: safeInput },
    pause,
  });
  const { data, fetching, error } = result;
  return { data, fetching: pause ? false : fetching, error, reexecuteQuery };
};
